// Angular
import { Observable, share, takeUntil } from 'rxjs';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

// Auth
import { AuthService } from '../../../../../auth';

// Libraries

import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatDialog } from '@angular/material/dialog';
import { Select, Store } from '@ngxs/store';

import { TranslateService } from '@ngx-translate/core';

// Models
import { AppCleanup, ColorPickerChoice, ColorPickerType, Colors, LineStyleOption, LineStyles, LineWeights } from '../../../../../../shared/models';
import { JobCommentDto } from '../../../../models/job-comment-dto';
import { Job, JobData, JobUpdate, UserUnitPeriodChannelValue } from '../../../../models';
import { JobCommentDefaultGroupDto } from '../../../../../comment/models/job-comment-default-group-dto';
import { JobCommentDefaultDto } from '../../../../../comment/models/job-comment-default-dto';
import { UserUnitPeriodChannelDto } from '../../../../../channel/models/user-unit-period-channel-dto';
import { JobTypeChannelDto } from '../../../daily-report/models/job-type-channel-dto';
import { JobCommentDefaultResponseAllDto } from '../../../../../comment/models/job-comment-default-response-all-dto';

// Services
import { JobService } from '../../../../services/job.service';
import { DashboardService } from '../../../dashboard/services/dashboard.service';
import { TimezoneService } from '../../../../services/timezone.service';
import { SubheaderService } from '../../../../../../shared/components/subheader/services/subheader.service';
import { DialogService } from '../../../../../../shared/services/dialog.service';

// Components
import { ColorPickerComponent, ColorPickerData } from '../../../../../../shared/components';
import { CommentAddDialogComponent } from '../../../daily-report/dialogs/comment-add-dialog/comment-add-dialog.component';

// Enums
import { Roles } from '../../../../../auth/models/roles.enum';
import { ChangeType } from '../../../../enums/change-type.enum';

// Hub
import { JobHub } from '../../../../services/job.hub';

// Store
import { CommentDataState } from '../../../../../comment/state/comment.state';
import { CommentDataActions } from '../../../../../comment/state/comment.action';
import { AuthState } from '../../../../../auth/state/auth.state';

// Helpers
import { UtilityHelper } from '../../../../../../shared/helpers';
import { ConfirmDialogModel } from '../../../../../../shared/dialogs/confirm-dialog/confirm-dialog.component';

@Component({
	selector: 'job-overview-mobile',
	templateUrl: './job-overview-mobile.component.html',
	styleUrls: ['job-overview-mobile.component.scss'],
})
export class JobOverviewMobileComponent extends AppCleanup implements OnInit, OnDestroy {
	@HostBinding('class') classes = 'component-container';
	@Select(AuthState.isLoggedIn) isLoggedIn$: Observable<boolean>;
	@Select(CommentDataState.isLoaded) commentDataLoaded$: Observable<boolean>;
	@Select(CommentDataState.commentData) commentData$: Observable<JobCommentDefaultResponseAllDto>;
	@Input() key: string; /** Job Key */
	@Input() allowCommentVisibility: boolean = true; /** Allow Comments to be visible */
	jobKey: string; /**Equal to Input key if set, otherwise retrieved from route snapshot id */
	_presetColors: Array<string> = [...Colors.ChartDefaults];
	fabTimeout: NodeJS.Timeout;
	_selectDateAction = '';
	_selectedJobComment: JobCommentDto;
	_jobInitialized = false;
	job: Job;
	isPublic: boolean; /**If true, the Job is viewable to the Public through a snapshot key. If false, you must be logged in to see the job*/
	userJobChannelValuesToShow: UserUnitPeriodChannelValue[] = [];
	initializingData: boolean = true;
	lineStyleOptions: Array<LineStyleOption> = [...LineStyles.List];
	lineWeightOptions: Array<number> = [...LineWeights.List];
	disableDragging = true;
	channelSettingView = false;
	editingFromFab = false;
	userJobChannelValues: UserUnitPeriodChannelValue[];
	lastUpdatedDate: Date;
	jobInitialized = false;
	lastDataPerUnitPeriod: JobData[] = [];
	jobId: number;
	isAdmin: boolean;
	sortedJobComments: JobCommentDto[] = [];
	jobCommentDefaultGroups: JobCommentDefaultGroupDto[] = [];
	jobCommentDefaults: JobCommentDefaultDto[] = [];
	_bufferedLiveData: JobData[] = [];
	unitPeriodError = false;

	constructor(
		public dashboardService: DashboardService,
		private authService: AuthService,
		private subheaderService: SubheaderService,
		private timezoneService: TimezoneService,
		private dialog: MatDialog,
		public jobService: JobService,
		private changeDetectorRef: ChangeDetectorRef,
		private jobHub: JobHub,
		public route: ActivatedRoute,
		private store: Store,
		private translateService: TranslateService,
		private dialogService: DialogService
	) {
		super();
	}

	ngOnInit() {
		this.isLoggedIn$.pipe(takeUntil(this.destroy)).subscribe((isLoggedIn) => {
			// No need for comment groups to initialize on public links
			if (isLoggedIn) {
				this.commentDataLoaded$.pipe(takeUntil(this.destroy)).subscribe((isLoaded) => {
					if (!isLoaded) {
						this.store.dispatch(new CommentDataActions.GetAll({ showSpinner: false, showToast: false }));
					}
				});
			}
		});

		this.initializeComponents();

		this.jobHub.dataReceived$.pipe(takeUntil(this.destroy)).subscribe((data: Array<JobData>) => {
			if (data && data.length > 0) {
				let refresh = false;
				let userUnitPeriodChannels = this.findUserUnitPeriodChannels();
				data.forEach((data) => {
					if (data.k === this.job?.key && data.c && data.c.length > 0) {
						data.c.forEach((ch) => {
							let found = userUnitPeriodChannels.find((channel) => channel?.unitPeriodChannel?.channel?.id === ch.i);
							if (!found) {
								refresh = true;
							}
						});
					}
				});
			}
		});
	}

	ngOnDestroy() {
		super.ngOnDestroy();
	}

	initializeComponents() {
		this.isPublic = this.route.snapshot.data && this.route.snapshot.data.hasOwnProperty('public') && this.route.snapshot.data.public;

		this.subheaderService.disabled$.next(false);

		this.authService
			.checkAccess([Roles.TenantAdmin, Roles.SuperAdmin, Roles.Admin], () => {
				return true;
			})
			.subscribe((isAdmin) => {
				this.isAdmin = isAdmin;
			});

		this.jobKey = this.key || this.route.snapshot.paramMap.get('jobId');

		if (!this.isPublic) {
			this.jobHub.connectionEstablished$.pipe(takeUntil(this.destroy)).subscribe((result) => {
				if (result) {
					this.jobHub.startSendingData([this.jobKey]);
				}
			});

			this.initializeJob();
			this.setIntervalForLiveData();
		} else {
			this.jobService
				.getJobIdFromKey(this.jobKey)
				.pipe(takeUntil(this.destroy))
				.subscribe((jobId) => {
					if (jobId) {
						this.jobId = jobId;

						//Needed for public links since Parent does not subscribe
						//this.jobHub.startSendingData([String(this.jobId)]);
						//this.jobHub.registerOnServerEvents();

						this.jobHub.connectionEstablished$.pipe(takeUntil(this.destroy)).subscribe((result) => {
							if (result) {
								this.jobHub.startSendingData([String(this.jobId)]);
							}
						});

						this.initializeJob();
						this.setIntervalForLiveData();
					}
				});
		}

		this.subscribeToEvents(); // TODO
	}

	setIntervalForLiveData(interval: number = 1000) {
		let lastDeepCopyOfLastJobData: JobData[] = [];

		setInterval(() => {
			if (lastDeepCopyOfLastJobData.length === 0 || lastDeepCopyOfLastJobData.length !== this.lastDataPerUnitPeriod.length) lastDeepCopyOfLastJobData = UtilityHelper.deepCopy(this.lastDataPerUnitPeriod);

			lastDeepCopyOfLastJobData.forEach((jobData) => {
				let lastJobDataForUnitPeriod = this.lastDataPerUnitPeriod.find((ljd) => ljd.u === jobData.u);

				if (lastJobDataForUnitPeriod && jobData.d < lastJobDataForUnitPeriod.d) {
					let deepCopyOfLastJobData = UtilityHelper.deepCopy(lastJobDataForUnitPeriod);

					if (deepCopyOfLastJobData) {
						deepCopyOfLastJobData.c.forEach((c) => {
							let unitPeriodId = this.userJobChannelValuesToShow.find((ujc) => ujc.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId === deepCopyOfLastJobData.u)?.userUnitPeriodChannel?.unitPeriodChannel?.unitPeriodId;
							let findIndex = this.userJobChannelValuesToShow.findIndex((ujc) => ujc.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId === unitPeriodId && ujc.userUnitPeriodChannel.unitPeriodChannel.channelId === c.i);

							if (findIndex >= 0) {
								this.userJobChannelValuesToShow[findIndex].value = c.v;
							}
						});

						this.lastUpdatedDate = deepCopyOfLastJobData.d;
					}

					let findIndex = lastDeepCopyOfLastJobData.findIndex((ldc) => ldc.u === jobData.u);

					if (findIndex >= 0) {
						lastDeepCopyOfLastJobData[findIndex] = deepCopyOfLastJobData;
					}

					this.changeDetectorRef.markForCheck();
				}
			});

			if (this._bufferedLiveData.length > 0) {
				// transform the data
				let newData = this.jobService.applyOffsetsToDataPoints(this.jobKey, this._bufferedLiveData);
				newData = this.jobService.applyPrecisionToDataPoints(this.jobKey, newData);

				// update most recent unit period data
				this.lastDataPerUnitPeriod = this.updateDataByUnitPeriodId(this.lastDataPerUnitPeriod, newData);

				newData = this.jobService.filterOutOfRangePoints(this.jobKey, newData);
			}

			this._bufferedLiveData = [];
		}, interval);
	}

	updateDataByUnitPeriodId(currentData: JobData[], newData: Array<JobData>): JobData[] {
		const updatedData = [...currentData];

		newData.forEach((newItem) => {
			let existingItemIndex = updatedData.findIndex((existingItem) => existingItem.u === newItem.u);

			if (existingItemIndex < 0) {
				updatedData.push(newItem);
			} else {
				updatedData[existingItemIndex] = newItem;
			}
		});

		return updatedData;
	}

	/**Initializes all Job Data and fires the jobChange$ observable. This initializes the Job data for all child components as well.
	 * If the Job is ready or has changed, the jobChange$ observable will refresh the chart with a new API call.
	 */
	initializeJob() {
		// Make a very basic call to the Job Table
		if (this.isPublic && !this._jobInitialized) {
			this.jobService.initPublicJob(this.jobKey);
		}

		let waitingForJob = true;

		this.jobService
			.getJobStream(String(this.jobId))
			.pipe(takeUntil(this.destroy))
			.subscribe((jobUpdate: JobUpdate) => {
				if (jobUpdate) {
					// Because I don't know why
					if (jobUpdate.changeType === ChangeType.INITIAL && this.isPublic && this._jobInitialized) return;

					if (jobUpdate.changeType === ChangeType.INITIAL || waitingForJob) {
						this._jobInitialized = true;
						waitingForJob = false;
						this.jobInitialized = true;
						this.job = jobUpdate.job;
						this.mergeChannels();
						this.setComments();
						this.initializingData = false;
					} else if (jobUpdate.changeType === ChangeType.SYNC_CHANGE) {
						this.job.lastSyncRequest = jobUpdate.job.lastSyncRequest;
					} else {
						this.job = jobUpdate.job;
						this.mergeChannels();
						this.setComments();
						this.filterJobCommentDefaults();
						this.initializingData = false;

						if (jobUpdate.changeType === ChangeType.USER_UNIT_PERIOD_CHANNEL) {
							// Need to now completely redraw/show axises since potentially could be 30+
						}
					}

					this.commentData$.pipe(takeUntil(this.destroy)).subscribe((commentData) => {
						if (commentData) {
							let allJobCommentDefaultGroups = UtilityHelper.deepCopy(commentData?.jobCommentDefaultGroups);
							this.jobCommentDefaultGroups = allJobCommentDefaultGroups.filter((jcdg) => jcdg.jobTypeId === this.job?.jobTypeId);
							this.filterJobCommentDefaults();
						}
					});
				}
			});

		// this.jobService.jobChange$.pipe(takeUntil(this.destroy)).subscribe((jobChange) => {
		// 	if (jobChange && (jobChange.job.key == this.jobKey || this.isPublic)) {
		// 		// Because I don't know why
		// 		if (jobChange.changeType === ChangeType.INITIAL && this.isPublic && this._jobInitialized) return;

		// 		if (jobChange.changeType === ChangeType.INITIAL || waitingForJob) {
		// 			this._jobInitialized = true;
		// 			waitingForJob = false;
		// 			this.jobInitialized = true;
		// 			this.job = jobChange.job;
		// 			this.mergeChannels();
		// 			this.setComments();
		// 			this.initializingData = false;
		// 		} else if (jobChange.changeType === ChangeType.SYNC_CHANGE) {
		// 			this.job.lastSyncRequest = jobChange.job.lastSyncRequest;
		// 		} else {
		// 			this.job = jobChange.job;
		// 			this.mergeChannels();
		// 			this.setComments();
		// 			this.filterJobCommentDefaults();
		// 			this.initializingData = false;

		// 			if (jobChange.changeType === ChangeType.USER_UNIT_PERIOD_CHANNEL) {
		// 				// Need to now completely redraw/show axises since potentially could be 30+
		// 			}
		// 		}

		// 		this.commentData$.pipe(takeUntil(this.destroy)).subscribe((commentData) => {
		// 			if (commentData) {
		// 				let allJobCommentDefaultGroups = UtilityHelper.deepCopy(commentData?.jobCommentDefaultGroups);
		// 				this.jobCommentDefaultGroups = allJobCommentDefaultGroups.filter((jcdg) => jcdg.jobTypeId === this.job?.jobTypeId);
		// 				this.filterJobCommentDefaults();
		// 			}
		// 		});
		// 	}
		// });
	}

	displayTimeZone(date: Date): Date {
		return this.timezoneService.timeZoneDate(date, this.job?.utcDateTimeOffset || this.timezoneService.getCurrentTimeZone());
	}

	setComments() {
		this.sortedJobComments = UtilityHelper.deepCopy(
			this.job.jobCommentReturn.jobComments
				.filter((jobComment) => !jobComment.isDeleted && (!jobComment.hideInPortal || this.isAdmin))
				.sort((a, b) => {
					return new Date(b.commentDate).getTime() - new Date(a.commentDate).getTime();
				})
		);

		this.sortedJobComments.forEach((sjc) => {
			sjc.jobCommentChannels = sjc.jobCommentChannels.filter((jobCommentChannel) => jobCommentChannel.value !== null);
		});
	}

	getHexColorForChannel(jobTypeChannel: JobTypeChannelDto): string {
		if (this.job && jobTypeChannel?.channelId) {
			let userJobChannel = this.findUserUnitPeriodChannels().find((ujc) => ujc.unitPeriodChannel.channelId === jobTypeChannel?.channelId);

			if (userJobChannel?.colorHex) {
				return userJobChannel.colorHex;
			}
		}

		return 'black';
	}

	/**
	 * For public job's, the User Job Channels will be the same as Job Channels since there is no concept of a User.
	 * This method also determines which Channels to show in the Channel Display based on the User Job Channels.
	 */
	mergeChannels() {
		let tempCopyOfChannelValues = UtilityHelper.deepCopy(this.userJobChannelValues);
		this.userJobChannelValues = [];

		this.findUserUnitPeriodChannels().forEach((uupc) => {
			//Getting currentValue of channel so that channel display does not blank out on Job Change
			let currentValue = null;

			if (tempCopyOfChannelValues?.length > 0) {
				currentValue = tempCopyOfChannelValues.find((tc) => tc.userUnitPeriodChannel?.id === uupc.id)?.value;
			}

			let userJobChannelValue: UserUnitPeriodChannelValue = {
				userUnitPeriodChannel: uupc,
				value: currentValue,
			};
			this.userJobChannelValues.push(userJobChannelValue);
		});

		this.userJobChannelValues.sort((a, b) => {
			return a.userUnitPeriodChannel.seq - b.userUnitPeriodChannel.seq;
		});

		this.userJobChannelValuesToShow = this.userJobChannelValues.filter((userJobChannelValue) => userJobChannelValue.userUnitPeriodChannel.display).slice(0, 12);
	}

	drop(event: CdkDragDrop<string[]>) {
		if (event.previousIndex === event.currentIndex) {
			return;
		}

		//this.resetTimeoutHandle();
		moveItemInArray(this.jobService.findUnitPeriodChannelsForJob(this.job.key), event.previousIndex, event.currentIndex);

		let userChannelViewModels: UserUnitPeriodChannelDto[] = [];
		let i = 0;
		this.jobService.findUnitPeriodChannelsForJob(this.job.key).forEach((jcv) => {
			jcv.channel.seq = i;
			let channelToUpdate: UserUnitPeriodChannelDto = this.findUserUnitPeriodChannels().find((uc) => uc.unitPeriodChannel.unitPeriodId === jcv.unitPeriodId && uc.unitPeriodChannel.channelId === jcv.channel.id);
			if (!channelToUpdate) {
				channelToUpdate = new UserUnitPeriodChannelDto();
			}
			channelToUpdate.seq = jcv.channel.seq;
			userChannelViewModels.push(channelToUpdate);
			i++;
		});

		this.jobService
			.putUserJobChannelSequences(this.jobKey, userChannelViewModels)
			.pipe(takeUntil(this.destroy))
			.subscribe(() => {});

		this.mergeChannels();
	}

	/**
	 * Creates a UserJobChannelDTO and calls the API to save Channel changes for User
	 * @param channelValue Job Channel that is being altered
	 */
	modifyChannel(channelValue: UserUnitPeriodChannelValue) {
		//this.resetTimeoutHandle();
		let channelToUpdate: UserUnitPeriodChannelDto = this.findUserUnitPeriodChannels().find((uc) => uc.unitPeriodChannel.id === channelValue.userUnitPeriodChannel.unitPeriodChannel.id);
		channelToUpdate.display = channelValue.userUnitPeriodChannel.display;
		channelToUpdate.showGraph = channelValue.userUnitPeriodChannel.showGraph;
		channelToUpdate.colorHex = channelValue.userUnitPeriodChannel.colorHex;
		channelToUpdate.lineStyle = channelValue.userUnitPeriodChannel.lineStyle;
		channelToUpdate.lineWeight = channelValue.userUnitPeriodChannel.lineWeight;

		this.updateUserJobChannel(channelToUpdate);
	}

	/**
	 * Updates the User Job Channel through the Job Service
	 * @param jobChannelToUpdate - User Job Channel that is being altered
	 */
	updateUserJobChannel(jobChannelToUpdate: UserUnitPeriodChannelDto) {
		this.jobService.updateUserJobChannel(this.jobKey, jobChannelToUpdate);
	}

	/**
	 * Toggles the visibility of a channel on the Channel Display
	 * @param channel Job Channel that will be toggled on Display
	 */
	toggleChannelVisibility(channelValue: UserUnitPeriodChannelValue) {
		if (this.isPublic) return;

		channelValue.userUnitPeriodChannel.display = !channelValue.userUnitPeriodChannel.display;

		this.modifyChannel(channelValue);
	}

	/**
	 * Opens the Color Picker dialog to allow a user to change a channel's color
	 * @param channel Channel that needs its color to be changed
	 */
	openColorPicker(channelValue: UserUnitPeriodChannelValue) {
		if (this.isPublic) return;

		let validColors = this._presetColors.filter((pc) => {
			return !this.findUserUnitPeriodChannels().find(
				(c) =>
					c.colorHex === pc &&
					c.unitPeriodChannel.unitPeriodId !== channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId &&
					c.unitPeriodChannel.channel.id !== channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId
			);
		}, this);

		const dialogRef = this.dialog.open(ColorPickerComponent, {
			data: <ColorPickerData>{
				colors: validColors,
				label: channelValue.userUnitPeriodChannel.unitPeriodChannel.channel.label,
				type: ColorPickerType.Circle,
			},
			width: '100%',
			maxWidth: '300px',
			autoFocus: false,
		});

		dialogRef.componentInstance.colorChosen.pipe(takeUntil(this.destroy)).subscribe((result: ColorPickerChoice) => {
			channelValue.userUnitPeriodChannel.colorHex = result.color;
			this.modifyChannel(channelValue);
		});
	}

	/**
	 * Opens the Comment Dialog to Add/Edit a comment
	 * @param jobCommentDto Job Comment to modify
	 * @param ev Event from Chart that is used when a bullet-point is hit. This allows the Dialog to find an existing comment for editing.
	 */
	editComment(jobCommentDto: JobCommentDto, source: string, id: number) {
		if (id) {
			jobCommentDto = this.job.jobCommentReturn.jobComments.find((jc) => jc.id === id);
		}

		const dialogRef = this.dialog.open(CommentAddDialogComponent, {
			data: {
				jobComment: jobCommentDto,
				job: this.job,
				source: source,
				hideSaveActions: !this.isAdmin || this.isPublic,
			},
			width: '100%',
			maxWidth: this.isViewer ? '500px' : '450px',
			panelClass: 'comment-add-dialog-wrap',
			autoFocus: false,
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((result1: { action: string; jobComment: JobCommentDto }) => {
				if (result1) {
					if (result1.action == 'insert') {
						this.jobService.addJobComment(this.jobKey, result1.jobComment);
						let attachmentIds = result1.jobComment.jobAttachments.map((ja) => {
							return ja.id;
						});

						this.jobService.addJobAttachmentsToComment(this.job.id, result1.jobComment.id, attachmentIds).subscribe((result2) => {});
					} else if (result1.action == 'update') {
						this.jobService.updateJobComment(this.jobKey, result1.jobComment);
						let attachmentIds = result1.jobComment.jobAttachments.map((ja) => {
							return ja.id;
						});

						this.jobService.addJobAttachmentsToComment(this.job.id, result1.jobComment.id, attachmentIds).subscribe((result2) => {});
					} else if (result1.action == 'delete') {
						this.jobService.deleteJobComment(this.jobKey, result1.jobComment.id);
					}

					this.jobService.addComment = false;
				}
			});
	}

	deleteComment(jobCommentDto: JobCommentDto) {
		const confirmModel: ConfirmDialogModel = {
			title: 'Delete comment',
			message: 'Are you sure you want to delete this comment?',
			action: this.translateService.instant('ACTION.DELETE_CAPITAL'),
		};

		const dialogRef = this.dialogService.confirmAction(confirmModel);

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy))
			.subscribe((response) => {
				if (response) {
					this.jobService.deleteJobComment(this.jobKey, jobCommentDto.id);
				}
			});
	}

	/**
	 * For mobile view, looks at the value of the channel and applies a blinking red, or blinking yellow class to the channel if a threshold is exceeded
	 * @param channelValue The Channel Information and its current value
	 */
	getThresholdClass(channelValue: UserUnitPeriodChannelValue): string {
		let jobChannel = this.jobService
			.findUnitPeriodChannelsForJob(this.job.key)
			.find((jc) => jc.unitPeriodId == channelValue.userUnitPeriodChannel.unitPeriodChannel.unitPeriodId && jc.channelId == channelValue.userUnitPeriodChannel.unitPeriodChannel.channelId);

		if (jobChannel) {
			if ((jobChannel.lowDanger != null && channelValue.value <= jobChannel.lowDanger) || (jobChannel.highDanger != null && channelValue.value >= jobChannel.highDanger)) {
				return 'blink-red';
			} else if ((jobChannel.lowWarning != null && channelValue.value <= jobChannel.lowWarning) || (jobChannel.highWarning != null && channelValue.value >= jobChannel.highWarning)) {
				return 'blink-yellow';
			}
		}
		return '';
	}

	/**
	 * Subscribing to all Observables necessary for this Component.
	 */
	subscribeToEvents() {
		this.jobService.newDataSent$
			.pipe(share())
			.pipe(takeUntil(this.destroy))
			.subscribe((jobData) => {
				if (jobData && jobData.length > 0 && (jobData[0].k === this.jobKey || (this.jobId && jobData[0].k === String(this.jobId)))) {
					this._bufferedLiveData.push(...jobData);
				}
			});
	}

	/* When swiping on comments, this method will toggle the view of Channel values and de-toggle all other channel values*/
	toggleCommentValues(jobComment: JobCommentDto) {
		this.sortedJobComments.forEach((sortedJobComment) => {
			if (sortedJobComment.id !== jobComment.id) {
				sortedJobComment.expanded = false;
			}
		});
		jobComment.expanded = !jobComment.expanded;
	}

	filterJobCommentDefaults() {
		let selectedDefaultGroup = this.job.jobCommentReturn.filteredJobCommentDefaultGroups.find((jobCommentDefaultGroup) => jobCommentDefaultGroup.id === this.jobService.jobCommentDefaultGroupId);

		if (selectedDefaultGroup) {
			this.jobCommentDefaults = selectedDefaultGroup.jobCommentDefaults;
		} else {
			this.jobCommentDefaults = [];
		}
	}

	addJobComment(result: JobCommentDefaultDto) {
		let jobCommentDto: JobCommentDto = {
			id: 0,
			jobId: this.job.id,
			comment: '',
			overrideComment: result?.overrideComment,
			commentDate: new Date(),
			jobCommentDefaultId: result?.id,
			jobCommentDefault: result,
			hideInPortal: false,
			replacements: '',
		};

		this.editComment(jobCommentDto, '', null);
	}

	findUserUnitPeriodChannels(): UserUnitPeriodChannelDto[] {
		return this.jobService.findUserUnitPeriodChannelsForJob(this.job?.key);
	}
}
